import classNames from 'classnames';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { DistributionMore } from '@/modules/issuer-dashboard/components/distribution-dashboard/DistributionMore.tsx';
import { DividendOperationInfoModal } from '@/modules/issuer-dashboard/components/distribution-dashboard/DividendOperationInfoModal.tsx';
import { DistributionsHeaderAction } from '@/modules/issuer-dashboard/pages/IssuerDistributionsEvents.tsx';
import { useIssuerDividendEventListQuery } from '@/modules/issuer-dashboard/queries/dividends.tsx';
import {
  BodyCell,
  InfoCard,
  ModalProviderContext,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { ArrowLeftGayIcon, ArrowLeftIcon } from 'ui/icons';
import { goodFormatUSDCurrency, goodFormatUSDCurrencyLowPrecision } from 'ui/utils';
import { DividendStatus } from 'api';

type TypeHead = 'Name' | 'Status' | 'Date' | 'Class' | '$ per share/unit' | 'Total' | '';

interface PastDistributionsTableProps {
  isDeleted?: boolean;
}

export const PastDistributionsTable = ({ isDeleted = false }: PastDistributionsTableProps) => {
  const { isMobile } = useIsMobile();
  const [page, setPage] = useState<number>(1);
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { openModal } = useContext(ModalProviderContext);
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const { data, isFetching, isError } = useIssuerDividendEventListQuery({
    page,
    page_size: 5,
    status: DividendStatus.Completed,
    is_deleted: isDeleted,
  });

  const totalPage = Math.ceil((data?.total || 1) / (data?.page_size || 5));
  const hasNextPage = totalPage > page;
  const totalItems = data?.total || 0;

  const firstItemShownNumber = (page - 1) * 5 + 1;
  const lastItemShownNumber = Math.min((page - 1) * 5 + 5, totalItems);

  const isPossibleGoBack = !isFetching && page > 1;
  const isPossibleGoForward = !isFetching && hasNextPage;

  const goToPreviousPage = useCallback(() => {
    setPage(page => page - 1);
  }, []);

  const goToNextPage = useCallback(() => {
    setPage(page => page + 1);
  }, []);

  const headItems = useMemo<TableHeadColumn<TypeHead>[]>(() => {
    const items: TableHeadColumn<TypeHead>[] = [
      { title: 'Name', className: 'min-w-[140px] text-xs leading-5 text-[#737373] !py-2' },
      { title: 'Status', className: 'min-w-[140px] text-xs leading-5 text-[#737373] !py-2' },
      { title: 'Class', className: 'text-xs leading-5 text-[#737373] min-w-[140px] !py-2' },
      {
        title: 'Date',
        className: 'text-xs leading-5 text-[#737373] min-w-[170px] !py-2',
      },
      {
        title: '$ per share/unit',
        className: 'text-xs leading-5 text-[#737373] min-w-[140px] !py-2',
      },
      { title: 'Total', className: 'text-xs leading-5 text-[#737373] !py-2' },
      { title: '', className: 'min-w-[140px] !py-2' },
    ];

    return items;
  }, []);

  const handleOpenFull = useCallback(
    (id: number) => () => {
      openModal(DividendOperationInfoModal, {
        id,
        registerId,
        registerCountry,
        isDeleted,
      });
    },
    [isDeleted, openModal, registerCountry, registerId],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!data?.data?.length) {
      return [];
    }

    return data?.data?.map(data => {
      const body: BodyCell[] = [
        {
          cell: (
            <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">{data.name}</span>
          ),
          className: 'text-sm leading-[22px] !py-2',
        },
        {
          cell: (
            <span>
              <ActivityStatusBadge status={data.status || ''} />
            </span>
          ),
          className: '!py-2',
        },
        {
          cell: data.register_class?.class_name || '-',
          className: 'text-sm leading-[22px] !py-2',
        },
        {
          cell: dateToPrint(data.created_at, DateVariant.Table) || '-',
          className: 'text-sm leading-[22px] !py-2',
        },
        {
          cell: goodFormatUSDCurrency(data.amount_per_share),
          className: 'text-sm leading-[22px] !py-2',
        },
        {
          cell: goodFormatUSDCurrencyLowPrecision(data.total_amount || 0),
          className: 'text-sm leading-[22px] !py-2',
        },
        {
          cell: (
            <DistributionMore
              item={data}
              registerId={registerId}
              registerCountry={registerCountry}
              isDeleted={isDeleted}
            />
          ),
          className: 'text-sm leading-[22px] !py-2',
        },
      ];

      return {
        cells: body,
        onClick: handleOpenFull(data.id),
      };
    });
  }, [data?.data, handleOpenFull, isDeleted, registerCountry, registerId]);

  const bottomContent = !!bodyItems.length && (
    <div className="w-full flex flex-row justify-between">
      <div className="text-sm text-neutral-500">
        {firstItemShownNumber} - {lastItemShownNumber} of {totalItems}
      </div>

      <div className="flex flex-row gap-6 items-center">
        {isFetching && <Spinner className="w-6 h-6 min-w-[24px] min-h-[24px]" />}
        <button onClick={goToPreviousPage} disabled={!isPossibleGoBack}>
          <img
            className={classNames('w-6 h-6 min-w-6 min-h-6', {
              'hover:opacity-70 cursor-pointer': isPossibleGoBack,
            })}
            alt="back"
            src={isPossibleGoBack ? ArrowLeftIcon : ArrowLeftGayIcon}
          />
        </button>
        <button onClick={goToNextPage} disabled={!isPossibleGoForward}>
          <img
            alt="forward"
            src={isPossibleGoForward ? ArrowLeftIcon : ArrowLeftGayIcon}
            className={classNames('rotate-180 w-6 h-6 min-w-6 min-h-6', {
              'hover:opacity-70 cursor-pointer': isPossibleGoForward,
            })}
          />
        </button>
      </div>
    </div>
  );

  return (
    <InfoCard
      title={`Past ${getRegisterTypeStrings('dividend', { plural: true })}`}
      bottomAction={bottomContent}
      classes={{ content: 'p-0' }}
      headerAction={<DistributionsHeaderAction />}
    >
      <div className="flex flex-col pt-6 pb-2">
        {/* {!bodyItems?.length && ( */}
        {/*   <div className="flex justify-center items-center font-medium text-lg px-6 pb-4"> */}
        {/*     <ChartEmptyState isError={isError} isLoading={isFetching} /> */}
        {/*   </div> */}
        {/* )} */}
        {/* {!isLoading && bodyItems?.length > 0 && ( */}
        {/*   <> */}
        <div>
          <Table
            className="my-0 rounded-b-xl"
            classNameHead="bg-white border-t-transparent"
            withBorder={false}
            withRounded={false}
            withBorderX={isMobile}
            mobileFirstCellShowInHeadLeft={true}
            mobileLastCellShowInHeadRight={true}
            headItems={headItems}
            items={bodyItems}
            isLoading={isFetching}
            isError={isError}
          />
        </div>
        {/*   </> */}
        {/* )} */}
      </div>
    </InfoCard>
  );
};
